import React from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CaretLeftIcon } from '@/atoms/Icons/CaretLeftIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { EnvelopeIcon } from '@/atoms/Icons/EnvelopeIcon'
import { FacebookIcon } from '@/atoms/Icons/FacebookIcon'
import { LinkIcon } from '@/atoms/Icons/LinkIcon'
import { LinkedInLettersIcon } from '@/atoms/Icons/LinkedInLettersIcon'
import { RedditIcon } from '@/atoms/Icons/RedditIcon'
import { TelegramIcon } from '@/atoms/Icons/TelegramIcon'
import { TwitterXIcon } from '@/atoms/Icons/TwitterXIcon'
import { CaptionSM, LabelMD, ParagraphSM, TitleMD } from '@/atoms/Text'
import { IconColor, OneColorIconProps } from '@/atoms/utils'
import { useToast } from '@/molecules/Toast'
import { useTranslate } from '@/utils/translate/translate-client'

interface ConfirmationStepProps {
  handleGoBack: () => void
  onClose: () => void
  handleGiftConfirm: () => Promise<void>
  ticketQuantity: number
  projectName: string
  isLoading: boolean
  shareUrl: string
  color?: IconColor
}

interface SocialShare {
  name: string
  icon: React.FC<OneColorIconProps>
  href?: string
  onClick?: () => void
  brandColor: string
}

export const ShareLinkStep = ({
  handleGoBack,
  onClose,
  ticketQuantity,
  projectName,
  shareUrl,
  color,
}: ConfirmationStepProps) => {
  const { t } = useTranslate('common')

  const linkCopied = t('linkCopied', 'Link copied.')

  const toast = useToast()
  // const socials: Record<string, SocialShare> = {
  const socials = React.useMemo(
    () => ({
      copyLink: {
        name: t('copyLink', 'Copy Link'),
        icon: LinkIcon,
        onClick: () => {
          navigator.clipboard.writeText(shareUrl)
          toast?.showToast(linkCopied, { icon: <CopyIcon size={20} color="gray-200" /> })
        },
        brandColor: '#000',
      },
      email: {
        name: t('email', 'Email'),
        icon: EnvelopeIcon,
        href: `mailto:?subject=Join%20me%20on%20Angel%20Studios&body=Come%20and%20see%20this%20from%20Angel%20Studios:%0D%0D${encodeURIComponent(
          shareUrl,
        )}`,
        brandColor: '#000',
      },
      twitter: {
        name: 'Twitter',
        icon: TwitterXIcon,
        href: `https://twitter.com/share?url=${shareUrl}`,
        brandColor: '#1DA1F2',
      },
      facebook: {
        name: 'Facebook',
        icon: FacebookIcon,
        href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
        brandColor: '#1877F2',
      },
      reddit: {
        name: 'Reddit',
        icon: RedditIcon,
        href: `http://reddit.com/submit?url=${shareUrl}`,
        brandColor: '#FF4500',
      },
      telegram: {
        name: 'Telegram',
        icon: TelegramIcon,
        href: `https://telegram.me/share/url?url=${shareUrl}`,
        brandColor: '#27A7E7',
      },
      linkedIn: {
        name: 'LinkedIn',
        icon: LinkedInLettersIcon,
        href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
        brandColor: '#0A66C2',
      },
    }),
    [shareUrl, t, toast, linkCopied],
  )

  const socialLinks = Object.values(socials)
  const renderSocialIcon = ({ name, href, onClick, icon: Icon }: SocialShare) => {
    if (name === 'Copy Link') return null

    return (
      <div className="w-[66px]">
        <a
          key={name}
          className={classNames(
            'focus-visible:right-offset-0 flex flex-col items-center gap-2 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent',
          )}
          href={href}
          rel="noreferrer"
          target="_blank"
          onClick={onClick}
          aria-label={name}
        >
          <div className="rounded-full bg-black p-4">
            <Icon color={'white'} size={24} />
          </div>
          <div>
            <CaptionSM> {name}</CaptionSM>
          </div>
        </a>
      </div>
    )
  }

  return (
    <>
      <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-gray-300 p-2" onClick={onClose}>
        <CloseIcon color="black" size={16} />
      </div>
      <FlexColumn className="-mt-3 gap-8 pb-6 md:pb-8">
        <FlexColumn className="gap-2 text-left">
          <TitleMD weight={'semibold'}>{t('shareGuildTicketsModalHeading', 'Share My Tickets')}</TitleMD>
          <ParagraphSM
            weight={'medium'}
            className={'text-[13px] leading-[16px] text-core-gray-700 md:hidden'}
            color={'gray-700'}
          >
            {t(
              'giftTicketsWarning',
              `You're about to gift your ({{ numberOfCodes }}) two unused Premium Guild tickets to {{ projectName }}.`,
              {
                numberOfCodes: ticketQuantity,
                projectName: projectName,
              },
            )}
          </ParagraphSM>
        </FlexColumn>
      </FlexColumn>
      <LabelMD weight={'medium'} color={'gray-700'} className={'pb-4 text-left'}>
        Sharing Options
      </LabelMD>
      <FlexColumn className="mb-6 gap-8">
        <div className="grid grid-cols-6 gap-x-2">
          {socialLinks.filter((link) => link.name !== 'Copy Link').map(renderSocialIcon)}
        </div>
      </FlexColumn>
      <FlexColumn className="gap-8 pb-32">
        <div className="mt-2">
          <LabelMD className="mb-2 text-left text-sm">{t('pageLink', 'Page Link')}</LabelMD>
          <div className="flex items-center rounded-[14px] border border-gray-400 bg-gray-100/20 p-3">
            <input type="text" value={shareUrl} readOnly className="grow bg-transparent text-sm outline-none" />
            <button
              onClick={() => {
                socials.copyLink.onClick?.()
              }}
              className="ml-2 rounded p-1 hover:bg-gray-200"
              aria-label="Copy link"
            >
              <CopyIcon size={20} color={color} />
            </button>
          </div>
        </div>
      </FlexColumn>
      <FlexColumn className="grow-1 gap-2 pt-6">
        <FlexColumn className="items-left justify-start gap-2">
          <Button
            onClick={handleGoBack}
            className=" font-color-black items-left w-fit justify-start gap-1.5 rounded-[0.875rem] py-3.5 pl-5 pr-6 text-sm font-medium"
            variant="gray"
          >
            <CaretLeftIcon color={'black'} />
            <LabelMD>{t('back', 'Back')}</LabelMD>
          </Button>
        </FlexColumn>
      </FlexColumn>
    </>
  )
}
